/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Details/Source/ServiceEndpointsSource" />
import { SecurityNamespaceConstants } from "DistributedTaskUI/SecurityComponent/Constants";
import {
    ServiceEndpoint,
    ServiceEndpointProjectReference,
    ServiceEndpointExecutionRecord,
    ServiceEndpointType,
    ServiceEndpointAuthenticationScheme,
    ServiceEndpointRequest,
    ServiceEndpointRequestResult
} from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import * as EndpointConstants from "ServiceEndpoints/Client/Constants/ServiceEndpoint";
import { IServiceEndpointRestClient } from "ServiceEndpoints/Client/RestClient/ServiceEndpoint";
import { deepMerge } from "VSS/Platform/Util/Object";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";
import { IdentityRefWithEmail, PagedList } from "VSS/Platform/Contracts/WebApi";
import { IVssIdentityService, VssIdentityServiceName, IIdentity } from "VSS/Features/Identities/IdentityService";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssSecurityService } from "VSS/Platform/Security";

export interface IServiceEndpointDetails {
    serviceEndpoint: ServiceEndpoint;
    serviceEndpointExecutionHistory: ServiceEndpointExecutionRecord[];
    serviceEndpointType: ServiceEndpointType;
    eligibleSchemaUpgrade: ServiceEndpointAuthenticationScheme;
    eligibleSchemaDowngrade: ServiceEndpointAuthenticationScheme;
}

export class ServiceEndpointsSource {
    public static getInstance(pageContext: IVssPageContext) {
        if (!ServiceEndpointsSource._instance) {
            ServiceEndpointsSource._instance = new ServiceEndpointsSource(pageContext);
        }
        return ServiceEndpointsSource._instance;
    }

    private constructor(private _pageContext: IVssPageContext) {
        this._identityService = this._pageContext.getService<IVssIdentityService>(VssIdentityServiceName);
        this._serviceEndpointRestClient = this._pageContext.getRestClient<IServiceEndpointRestClient>("ServiceEndpoint.IServiceEndpointRestClient");
        const tfsPageData = getTFSPageData(this._pageContext);
        this._serviceEndpointTypesCache = {};
        if (tfsPageData && tfsPageData.project) {
            this._projectId = tfsPageData.project.id;
            this._projectName = tfsPageData.project.name;
        }
    }

    public getIdentityRefWithEmail(id: string): Promise<IdentityRefWithEmail[]> {
        return this._identityService
            .searchIdentitiesAsync(id, ["user"], ["ims", "source"], "uid", undefined, undefined)
            .then(
                (identities: IIdentity[]) =>
                    identities
                        .map((identity) => this._toIdentityRefWithEmail(identity))
                        .filter((item) => item !== undefined) as IdentityRefWithEmail[]
            );
    }

    public getServiceEndpointsHubData(endpointId: string): Promise<IServiceEndpointDetails> {
        const parameters: { [key: string]: string } = {};
        parameters["serviceEndpointId"] = endpointId;
        parameters["projectId"] = this._projectId;

        return this._pageContext
            .getService<IVssContributionService>("IVssContributionService")
            .getDataAsync<IServiceEndpointDetails>("ms.vss-serviceEndpoints-web.service-endpoints-details-data-provider", parameters, true)
            .then(
                (data: IServiceEndpointDetails) => {
                    if (!data) {
                        return null;
                    }

                    return data;
                },
                (error: Error) => {
                    throw error;
                }
            );
    }

    public executeServiceEndpointRequest(
        serviceEndpointRequest: ServiceEndpointRequest,
        serviceEndpointId: string
    ): Promise<ServiceEndpointRequestResult> {
        return this._serviceEndpointRestClient.executeServiceEndpointRequest(serviceEndpointRequest, this._projectId, serviceEndpointId);
    }

    public deleteServiceEndpoint(endpointId: string, deep?: boolean): Promise<void> {
        let projectIds: string[] = [];
        projectIds.push(this._projectId);
        return this._serviceEndpointRestClient.deleteServiceEndpoint(endpointId, projectIds, deep);
    }

    public disableServiceEndpoint(endpointId: string, endpoint: ServiceEndpoint): Promise<ServiceEndpoint> {
        endpoint.isDisabled = true;
        return this._serviceEndpointRestClient.updateServiceEndpoint(endpoint, endpointId, EndpointConstants.AzureEndpointConstants.ChangePropertiesOperation);
    }

    public enableServiceEndpoint(endpointId: string, endpoint: ServiceEndpoint): Promise<ServiceEndpoint> {
        endpoint.isDisabled = false;
        return this._serviceEndpointRestClient.updateServiceEndpoint(endpoint, endpointId,  EndpointConstants.AzureEndpointConstants.ChangePropertiesOperation);
    }

    public verifyServiceEndpoint(endpointRequest: ServiceEndpointRequest, endpointId = ""): Promise<ServiceEndpointRequestResult> {
        return this._serviceEndpointRestClient.executeServiceEndpointRequest(endpointRequest, this._projectId, endpointId);
    }

    public convertServiceEndpointAuthenticationSchemeToSecret(
        serviceEndpoint: ServiceEndpoint,
        scheme: ServiceEndpointAuthenticationScheme,
        values: { [key: string]: string }
    ): Promise<ServiceEndpoint> {
        const changedEndpoint = deepMerge({}, serviceEndpoint);

        let currentProjectReference: ServiceEndpointProjectReference = {
            description: changedEndpoint.description,
            name: changedEndpoint.name,
            projectReference: {
                id: this._projectId,
                name: this._projectName
            }
        };
        changedEndpoint.serviceEndpointProjectReferences = [currentProjectReference];
        changedEndpoint.name = null;
        changedEndpoint.description = null;
        changedEndpoint.authorization.scheme = scheme.scheme;

        // drop all auth parameters not required for the new scheme
        const inputDescriptorsDict: { [key: string]: any } = {};
        scheme.inputDescriptors.forEach((inputDescriptor) => {
            inputDescriptorsDict[inputDescriptor.id] = inputDescriptor;
        });

        for (const key in serviceEndpoint.authorization.parameters) {
            if (!inputDescriptorsDict[key]) {
                delete changedEndpoint.authorization.parameters[key];
            }
        }

        // add the ones required if not present
        for (const key in inputDescriptorsDict) {
            if (values[key]) {
                changedEndpoint.authorization.parameters[key] = values[key];
            }
        }


        delete changedEndpoint.data[EndpointConstants.AzureEndpointConstants.AppObjectId];
        delete changedEndpoint.data[EndpointConstants.AzureEndpointConstants.SpnObjectId];

        // even if this service connection used to be automatic, make it manual by design
        changedEndpoint.data[EndpointConstants.AzureEndpointConstants.SpnCreationMode] = EndpointConstants.AzureEndpointConstants.SpnCreationModeManual;

        return this._serviceEndpointRestClient.updateServiceEndpoint(changedEndpoint, changedEndpoint.id, "");
    }

    public convertServiceEndpointAuthenticationScheme(
        serviceEndpoint: ServiceEndpoint,
        scheme: ServiceEndpointAuthenticationScheme
    ): Promise<ServiceEndpoint> {
        let currentProjectReference: ServiceEndpointProjectReference = {
            description: serviceEndpoint.description,
            name: serviceEndpoint.name,
            projectReference: {
                id: this._projectId,
                name: this._projectName
            }
        };
        const upgradedEndpoint = {
            id: serviceEndpoint.id,
            type: serviceEndpoint.type,
            authorization: {
                scheme: scheme.scheme
            },
            serviceEndpointProjectReferences: [currentProjectReference]
        } as ServiceEndpoint;

        return this._serviceEndpointRestClient.updateServiceEndpoint(
            upgradedEndpoint,
            upgradedEndpoint.id,
            EndpointConstants.AzureEndpointConstants.ConvertAuthenticationSchemeOperation
        );
    }

    public getServiceEndpoint(endpointId: string): Promise<ServiceEndpoint> {
        return this._serviceEndpointRestClient.getServiceEndpointDetails(this._projectId, endpointId);
    }

    public getServiceEndpointExecutionRecords(
        endpointId: string,
        top?: number,
        continuationToken?: number
    ): Promise<PagedList<ServiceEndpointExecutionRecord>> {
        return this._serviceEndpointRestClient.getServiceEndpointExecutionRecords(this._projectId, endpointId, top, continuationToken);
    }

    public checkManageEndpointPermission(endpointId: string, managePermission: number): boolean {
        let token = this._getSecurityToken(endpointId);
        return this._pageContext
            .getService<IVssSecurityService>("IVssSecurityService")
            .hasPermission(SecurityNamespaceConstants.NamespaceId, token, managePermission);
    }

    public checkManageProjectSharingPermission(endpointId: string, managePermission: number): boolean {
        let token = this._getCollectionSecurityToken(endpointId);
        return this._pageContext
            .getService<IVssSecurityService>("IVssSecurityService")
            .hasPermission(SecurityNamespaceConstants.NamespaceId, token, managePermission);
    }

    public checkProjectLevelManagePermission(managePermission: number): boolean {
        let token = SecurityNamespaceConstants.Endpoints.concat(SecurityNamespaceConstants.NamespaceSeparator, this._projectId);
        return this._pageContext
            .getService<IVssSecurityService>("IVssSecurityService")
            .hasPermission(SecurityNamespaceConstants.NamespaceId, token, managePermission);
    }

    private _toIdentityRefWithEmail(identity?: IIdentity): IdentityRefWithEmail | undefined {
        if (!identity || !identity.subjectDescriptor) {
            return undefined;
        }

        return {
            descriptor: identity.subjectDescriptor,
            id: identity.localId || identity.originId,
            displayName: identity.displayName,
            uniqueName: identity.samAccountName ? `${identity.scopeName}\\${identity.samAccountName}` : identity.signInAddress || identity.mail,
            _links: {
                avatar: {
                    href: identity.image
                }
            },
            preferredEmailAddress: identity.mail
        } as IdentityRefWithEmail;
    }

    private _getSecurityToken(endpointId: string) {
        let token = SecurityNamespaceConstants.Endpoints.concat(SecurityNamespaceConstants.NamespaceSeparator, this._projectId);
        token = token.concat(SecurityNamespaceConstants.NamespaceSeparator, endpointId);

        return token;
    }

    private _getCollectionSecurityToken(endpointId: string) {
        let token = SecurityNamespaceConstants.Endpoints.concat(SecurityNamespaceConstants.NamespaceSeparator, SecurityNamespaceConstants.Collection);
        token = token.concat(SecurityNamespaceConstants.NamespaceSeparator, endpointId);

        return token;
    }

    private _identityService: IVssIdentityService;
    private _serviceEndpointRestClient: IServiceEndpointRestClient;
    private _projectId: string;
    private _projectName: string;
    private _serviceEndpointTypesCache: { [key: string]: Promise<ServiceEndpointType[]> };
    private static _instance: ServiceEndpointsSource = null;
}
